import { useEffect, useState, useRef } from "react";
import BaseMapComponent from "../../Components/Map/BaseMap";
import { Marker } from "react-map-gl";
import { useNavigate } from "react-router-dom";
import * as turf from "@turf/turf";
import DropdownMenu from "../../helpers/DropdownMenu";
import { CustomTooltip, distributionStatuses } from "../../helpers/Constants";
import Dropdown from "../../helpers/Dropdown";
import { chartData4 } from "../../helpers/Utils";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Legend } from "chart.js";
import { showNotificationToast } from "../../helpers/Messages";
import InfoPopup from "../inspections/InfoPopup";
import Reset from "../../assets/img/icons/reset.png";
import Search from "../../assets/img/search.svg";
import { Tooltip } from "@mui/material";

const INITIAL_PARENT_HEIGHT = 600;
const INITIAL_MAP_ZOOM = 14;
const MIN_ZOOM_TO_SHOW_MARKERS = 16;

const DistributionMap = ({ id }) => {
  const navigate = useNavigate();

  const user_email = localStorage.getItem("user_email");
  const userRole = localStorage.getItem("user_role");
  const role_condition = userRole === "Admin" || userRole === "Operations";

  const mapRef = useRef(null);
  const parentRef = useRef(null);
  const inputRef = useRef(null);

  const [parentHeight, setParentHeight] = useState(INITIAL_PARENT_HEIGHT);

  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [visibleMarkers, setVisibleMarkers] = useState([]);
  const [mapZoom, setMapZoom] = useState(INITIAL_MAP_ZOOM);

  const [polygonCoordinates, setPolygonCoordinates] = useState([]);
  const [drawMode, setDrawMode] = useState(false);
  const [selectedStructureCount, setSelectedStructureCount] = useState(0);

  const [hoveredMarker, setHoveredMarker] = useState(null);

  const [filteredAnalysts, setFilteredAnalysts] = useState([]);
  const [filteredReviewers, setFilteredReviewers] = useState([]);

  const [allAnalystsList, setAllAnalystsList] = useState([]);
  const [allReviewersList, setAllReviewersList] = useState([]);

  const [assignedAnalyst, setAssignedAnalyst] = useState(null);
  const [assignedReviewer, setAssignedReviewer] = useState(null);

  const [openPopup, setOpenPopup] = useState(false);

  const [highlightedMarkers, setHighlightedMarkers] = useState([]);

  const [selectedStats, setSelectedStats] = useState([]);
  const [search, setSearch] = useState("");
  const [distributionStatsList, setDistributionStatsList] = useState({});

  const [statusUpdate, setStatusUpdate] = useState(false);

  const [latLngInput, setLatLngInput] = useState("");
  const [highlightedPoint, setHighlightedPoint] = useState(null);

  const handleStatusCheck = (item) => {
    setSelectedStats((prevSelected) =>
      prevSelected.includes(item)
        ? prevSelected.filter((i) => i !== item)
        : [...prevSelected, item]
    );
  };

  useEffect(() => {
    const handleResize = () => {
      if (parentRef.current) {
        setParentHeight(parentRef.current.clientHeight);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const updateRemoteData = async () => {
    setLoading(true);
    try {
      const data = await window.apiHelper.getDistributionStructuresList(
        id,
        search,
        filteredAnalysts,
        filteredReviewers,
        selectedStats
      );
      data.length === 0 && setVisibleMarkers([]);
      setMapData(data);
    } catch (err) {
      if (err.response?.status === 404) {
        navigate("/accessdenied");
      }
    } finally {
      window.appComponent.setState({
        loaderVisible: false,
      });
      setLoading(false);
      setStatusUpdate(false);
    }
  };

  useEffect(() => {
    updateRemoteData();
  }, [
    search,
    filteredAnalysts,
    filteredReviewers,
    selectedStats,
    statusUpdate,
  ]);

  const getData = async () => {
    setLoading(true);
    try {
      const distribution_stats = await window.apiHelper.getDistributionStats(
        id
      );
      setDistributionStatsList(distribution_stats);

      const analysts = await window.apiHelper.getAnalysts();
      const reviewers = await window.apiHelper.getReviewers();

      const analystsData = analysts.map(
        ({ id, name, email, structures_assigned }) => ({
          id,
          label: name,
          value: email,
          name: name,
          structures_assigned: structures_assigned,
        })
      );

      const reviewersData = reviewers.map(
        ({ id, name, email, structures_assigned }) => ({
          id,
          label: name,
          value: email,
          name: name,
          structures_assigned: structures_assigned,
        })
      );

      setAllAnalystsList(analystsData);
      setAllReviewersList(reviewersData);
    } catch (err) {
      if (err.response?.status === 404) {
        navigate("/accessdenied");
      }
    } finally {
    }
  };

  useEffect(() => {
    getData();
  }, [statusUpdate]);

  const handleMapClick = (event) => {
    if (hoveredMarker) {
      setHoveredMarker(null);
      return;
    }
  };

  const startDrawing = () => {
    setPolygonCoordinates([]);
    setDrawMode(!drawMode);
    setHighlightedMarkers([]);
    setSelectedStructureCount(0);
  };

  const calculateMarkersInside = (polygon) => {
    if (polygon.length < 3) {
      setSelectedStructureCount(0);
      setHighlightedMarkers([]);
      return;
    }

    const closedPolygon = [...polygon, polygon[0]];
    const polygonGeoJSON = turf.polygon([closedPolygon]);
    let insideCount = 0;
    let highlighted = [];

    visibleMarkers.forEach((marker) => {
      const coordinates = [marker.location.lon, marker.location.lat];
      const point = turf.point(coordinates);
      if (turf.booleanPointInPolygon(point, polygonGeoJSON)) {
        insideCount++;
        highlighted.push(marker);
      }
    });

    setSelectedStructureCount(insideCount);
    setHighlightedMarkers(highlighted);
  };

  useEffect(() => {
    calculateMarkersInside(polygonCoordinates);
  }, [polygonCoordinates]);

  const handleAnalystSelect = (selectedAnalyst) => {
    setFilteredAnalysts(selectedAnalyst);
  };

  const handleReviewerSelect = (selectedReviewer) => {
    setFilteredReviewers(selectedReviewer);
  };

  const handleStructureClick = async (structure) => {
    try {
      window.appComponent.setState({
        loaderVisible: true,
      });
      const images = await window.apiHelper.getImagesList(
        id,
        structure.structure_id
      );

      if (images.length > 0) {
        const imageDetails = await window.apiHelper.getPhotoAnnotationDetails(
          images[0].id
        );

        navigate(
          window.location.pathname +
            "/" +
            structure.name +
            "/" +
            structure.structure_id +
            "/" +
            imageDetails.id
        );
      } else {
        showNotificationToast("Structure has not been inspected yet");
      }
    } catch (e) {
      console.log(e);
    } finally {
      window.appComponent.setState({
        loaderVisible: false,
      });
    }
  };

  ChartJS.register(ArcElement, Legend);
  let chartOptions = {
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
  };

  const onMarkerClick = (structure) => {
    if (!drawMode) {
      handleStructureClick(structure);
    } else {
      handleShowAssignPopup(structure);
    }
  };

  const isShowInfoDisabled = (structure) => {
    const statusInfo = distributionStatuses[structure.status];
    return (
      statusInfo?.key === "pending" ||
      statusInfo?.key === "photos_uploaded" ||
      statusInfo?.key === "assigned"
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const inputValue = inputRef.current.value;
    setSearch(inputValue);
  };

  const resetFilters = () => {
    setFilteredAnalysts([]);
    setFilteredReviewers([]);
    setMapData(null);
    setSearch("");
    inputRef.current.value = "";
    setSelectedStats([]);
  };

  const handleSave = async () => {
    try {
      const formattedData = highlightedMarkers.map((marker) => ({
        id: marker.id,
        status: 3,
        analyst: assignedAnalyst[0].value,
        reviewer: assignedReviewer[0].value,
      }));

      const data = await window.apiHelper.bulkUpdateStructures(formattedData);

      if (data) {
        setStatusUpdate(true);
        setSelectedStructureCount(0);
        setHighlightedMarkers([]);
        setPolygonCoordinates([]);
      }
    } catch (error) {
      console.error("Error updating structures:", error);
    } finally {
    }
  };

  const handleShowAssignPopup = (structure) => {
    highlightedMarkers.push(structure);
    setSelectedStructureCount(1);
  };

  const shouldHighlightStructure = (structure) => {
    const analystsEmail = structure.analyst.email;
    const reviewersEmail = structure.reviewer.email;

    return analystsEmail === user_email || reviewersEmail === user_email;
  };

  const handleLatLngSearch = () => {
    const [lat, lng] = latLngInput
      .split(",")
      .map((coord) => parseFloat(coord.trim()));

    if (isNaN(lat) || isNaN(lng)) {
      alert("Invalid coordinates. Please enter in 'lat, lng' format.");
      return;
    }

    if (mapRef.current) {
      const map = mapRef.current.getMap();
      map.flyTo({ center: [lng, lat], zoom: 15, duration: 2000 });

      setHighlightedPoint({ lat, lng });

      setTimeout(() => {
        setHighlightedPoint(null);
      }, 5000);
    }
  };

  return (
    <div
      ref={parentRef}
      style={{
        position: "relative",
        height: "100%",
        boxShadow: drawMode ? "0 0 10px 3px #27c59b" : "none",
      }}
    >
      {openPopup && (
        <div style={{ position: "absolute", zIndex: "10" }}>
          <InfoPopup
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            selectedStructureId={hoveredMarker?.structure_id}
            selectedRequestId={hoveredMarker?.request_id}
            height={parentHeight}
          />
        </div>
      )}
      {role_condition && (
        <Tooltip title="Reset Filters" arrow placement="top">
          <button className="reset-button-distribution" onClick={resetFilters}>
            <img
              src={Reset}
              alt="reset"
              style={{ width: "25px", padding: "3px" }}
            />
          </button>
        </Tooltip>
      )}
      {selectedStructureCount > 0 && role_condition && (
        <div className="count-display">
          <div
            style={{
              padding: "5px 10px 0px",
              borderRadius: "5px",
            }}
          >
            Towers inside area: {selectedStructureCount}
          </div>

          <DropdownMenu
            placeholder={"Analyst"}
            options={allAnalystsList}
            selectedOption={setAssignedAnalyst}
          />

          <DropdownMenu
            placeholder={"Reviewer"}
            options={allReviewersList}
            selectedOption={setAssignedReviewer}
          />

          <button
            onClick={handleSave}
            className="show-info-button"
            style={{ width: "100px", margin: "8px", alignItems: "right" }}
          >
            Save
          </button>
        </div>
      )}

      <div className="distribution-stats-box">
        <div className="distribution-stats">
          <Doughnut
            data={chartData4([
              distributionStatsList?.pending,
              distributionStatsList?.photos_uploaded,
              distributionStatsList?.assigned,
              distributionStatsList?.accepted,
              distributionStatsList?.reshoot,
              distributionStatsList?.overviewed,
            ])}
            options={chartOptions}
          />
          <div className="structure-stats-small">
            {Object.values(distributionStatuses).map((item) => (
              <span
                key={item.label}
                style={{
                  color: item.color,
                  textDecoration: selectedStats.includes(item.value)
                    ? "underline"
                    : "none",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedStats.includes(item.value)}
                  onChange={() => handleStatusCheck(item.value)}
                  style={{
                    cursor: "pointer",
                  }}
                />
                {item.label}: {distributionStatsList?.[item.key] || 0}
              </span>
            ))}
          </div>
        </div>
        {role_condition && (
          <div className="distribution-filters-section">
            <div className="distribution-searches">
              <div className="distribution-search">
                <form
                  action=""
                  autoComplete="off"
                  className="form-group search-group search"
                  onSubmit={handleSearch}
                >
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Structure Search"
                    id="annotation-search"
                    ref={inputRef}
                  />
                  <button onClick={handleSearch}>
                    <img src={Search} alt="Search" width="17" height="16" />
                  </button>
                </form>
              </div>
              <div className="distribution-search">
                <form
                  action=""
                  autoComplete="off"
                  className="form-group search-group search"
                  onSubmit={handleSearch}
                >
                  <input
                    type="text"
                    placeholder="Enter lat, lng"
                    value={latLngInput}
                    onChange={(e) => setLatLngInput(e.target.value)}
                    className="form-control"
                  />
                  <button onClick={handleLatLngSearch}>
                    <img src={Search} alt="Search" width="17" height="16" />
                  </button>
                </form>
              </div>
            </div>
            <div className="distribution-dropdowns">
              <div className="multi-button-dropdown-analyst">
                <Dropdown
                  options={allAnalystsList}
                  optionSelected={handleAnalystSelect}
                  selectedOptions={filteredAnalysts}
                  label="Select Analyst"
                />
              </div>
              <div className="multi-button-dropdown-reviewer">
                <Dropdown
                  options={allReviewersList}
                  optionSelected={handleReviewerSelect}
                  selectedOptions={filteredReviewers}
                  label="Select Reviewer"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <BaseMapComponent
        innerRef={mapRef}
        mapData={mapData}
        loading={loading}
        mapToolHeight={parentHeight}
        onVisibleMarkersChange={setVisibleMarkers}
        setMapZoom={setMapZoom}
        onClick={handleMapClick}
        sourceComponent="opshub"
        drawMode={drawMode}
        setDrawMode={setDrawMode}
        onDrawComplete={setPolygonCoordinates}
        highlightedIds={highlightedMarkers.map((m) => m.id)}
      >
        {mapZoom >= MIN_ZOOM_TO_SHOW_MARKERS &&
          visibleMarkers.length > 0 &&
          visibleMarkers.map((structure) => {
            const isHovered = hoveredMarker?.id === structure?.id;
            const statusInfo = distributionStatuses[structure.status];
            const statusLabel = statusInfo?.label;
            const markerColor = statusInfo?.color;
            const dotMarker = (
              <div
                onMouseEnter={() => setHoveredMarker(structure)}
                className="dot-marker"
                style={{
                  backgroundColor: markerColor,
                  outline: shouldHighlightStructure(structure)
                    ? "3px solid orange"
                    : highlightedMarkers.some((m) => m.id === structure?.id)
                    ? "3px solid orange"
                    : "none",
                  outlineOffset: "1px",
                }}
              />
            );
            return (
              <Marker
                key={structure?.id}
                latitude={structure?.location?.lat}
                longitude={structure?.location?.lon}
                anchor="center"
                onClick={() => onMarkerClick(structure)}
              >
                {drawMode ? (
                  dotMarker
                ) : (
                  <CustomTooltip
                    title={
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "5px",
                        }}
                      >
                        <div>Str. Name: {structure.name}</div>
                        <div>Status: {statusLabel}</div>
                        <div>Reviewer: {structure.reviewer.name || "N/A"}</div>
                        <div>Analyst: {structure.analyst.name || "N/A"}</div>
                        <button
                          onClick={() => setOpenPopup(true)}
                          className={
                            isShowInfoDisabled(structure)
                              ? "show-info-disabled-button"
                              : "show-info-button"
                          }
                          disabled={isShowInfoDisabled(structure)}
                        >
                          Show Info
                        </button>
                      </div>
                    }
                    placement="bottom"
                  >
                    {dotMarker}
                  </CustomTooltip>
                )}
              </Marker>
            );
          })}

        {highlightedPoint && (
          <Marker
            latitude={highlightedPoint.lat}
            longitude={highlightedPoint.lng}
          >
            <div className="highlight-point" />
          </Marker>
        )}
      </BaseMapComponent>
    </div>
  );
};

export default DistributionMap;
